/**
 * A campaign's contacts, from the CSV the customer picks to the NDJSON file the campaign is created with: the CSV
 * parse, the column → variable mapping, the cleaning (every number to +E.164 with the campaign's default country),
 * and the checks the create dialog makes before it sends anything — the server's own, mirrored so a refusal is
 * never the first the customer hears of a rule (`supabase/functions/campaign/validation.ts`; the worker's line rules
 * are `api-backend/src/campaign/lines.ts`).
 */

import { type BusinessHoursConfig, validateBusinessHours } from "@/lib/business-hours";
import { normalizeToE164 } from "@/lib/phone-utils";

export type CsvRow = Record<string, string>;

/** Variable → the CSV column that feeds it. One column may feed several variables. */
export type HeaderMapping = Record<string, string>;

/** A refusal in the dialog's words: a toast's title and description. */
export interface Problem {
  title: string;
  description: string;
}

/** A contact as the file carries it, one JSON object a line. */
export interface Contact {
  phone_number: string;
  contact_name: string | null;
  custom_data: Record<string, string>;
}

export const PHONE_VARIABLE = "phone_number";
export const NAME_VARIABLE = "contact_name";

/** The owner's limits for one campaign's file (2026-10-28), as the function and the worker hold them. */
export const MAX_CONTACTS = 100_000;
export const MAX_FILE_BYTES = 52_428_800;
export const MAX_NAME_CHARS = 255;

const PHONE_WORDS = ["phone", "number", "mobile", "tel"];
const NAME_WORDS = ["name", "contact", "customer"];
/** An unmapped column that looks like the number or the name is not copied into custom_data. */
const UNMAPPED_SKIP_WORDS = ["phone", "number", "name", "contact"];

const squash = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
const mentions = (header: string, words: readonly string[]) => words.some((word) => squash(header).includes(word));
const unquote = (value: string) => value.replace(/^"|"$/g, "").trim();

/** One CSV line into its values: a comma outside double quotes ends a value, `""` inside them is a quote. Trimmed. */
export function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let value = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"' && quoted && line[i + 1] === '"') {
      value += '"';
      i++;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      values.push(value.trim());
      value = "";
    } else {
      value += char;
    }
  }
  values.push(value.trim());
  return values;
}

/**
 * A CSV file into its headers and rows. Blank lines are passed over; `\r\n`, `\n` and `\r` all end a line. A column
 * with no heading is left out, and every other value stays under its own heading. A header row and one data row at
 * least, or it throws the sentence the dialog shows.
 */
export function parseCsv(text: string): { headers: string[]; rows: CsvRow[] } {
  if (!text.trim()) throw new Error("CSV file is empty or invalid");
  const lines = text.split(/\r?\n|\r/).filter((line) => line.trim());
  if (lines.length < 2) throw new Error("CSV file must have at least a header row and one data row");

  const columns = parseCsvLine(lines[0])
    .map((header, index) => ({ header: unquote(header), index }))
    .filter(({ header }) => header);
  if (columns.length === 0) throw new Error("CSV file has no valid headers");

  const rows = lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    return Object.fromEntries(columns.map(({ header, index }) => [header, unquote(values[index] ?? "")]));
  });
  return { headers: columns.map(({ header }) => header), rows };
}

/** The variables a campaign fills for its agent: what the prompt uses, with the dial target first whether or not it does. */
export function campaignVariables(promptVariables: readonly string[]): string[] {
  return [PHONE_VARIABLE, ...promptVariables.filter((name) => name !== PHONE_VARIABLE)];
}

/**
 * A first guess at which column feeds which variable, so a well-named CSV needs no mapping at all. An exact match
 * wins; then the words people use for a phone or a name; then either name containing the other.
 */
export function autoMapHeaders(variables: readonly string[], headers: readonly string[]): HeaderMapping {
  const mapping: HeaderMapping = {};
  for (const variable of variables) {
    const match = headers.find((header) => {
      if (squash(header) === squash(variable)) return true;
      if (variable === PHONE_VARIABLE) return mentions(header, PHONE_WORDS);
      if (variable === NAME_VARIABLE) return mentions(header, NAME_WORDS);
      return squash(header).includes(squash(variable)) || squash(variable).includes(squash(header));
    });
    if (match) mapping[variable] = match;
  }
  return mapping;
}

/** A column in one variable's dropdown, saying which other variables already read it — a note, never a block. */
export function columnLabel(header: string, mapping: HeaderMapping, variable: string): string {
  const others = Object.keys(mapping).filter((other) => other !== variable && mapping[other] === header);
  return others.length > 0 ? `${header} (also used by: ${others.join(", ")})` : header;
}

/**
 * Why no column can supply the numbers to dial, or null when one can: a mapped column with a number in at least
 * one row, or no mapping but a column whose name says it holds them.
 */
export function phoneColumnProblem(mapping: HeaderMapping, headers: readonly string[], rows: readonly CsvRow[]): Problem | null {
  const column = mapping[PHONE_VARIABLE];
  if (!column) {
    return headers.some((header) => mentions(header, PHONE_WORDS))
      ? null
      : { title: "Phone number mapping required", description: "Please map a CSV column to 'phone_number'. This is required for making calls." };
  }
  return rows.some((row) => (row[column] ?? "").trim())
    ? null
    : { title: "No phone numbers found", description: `The mapped column "${column}" does not contain any phone numbers.` };
}

/**
 * CSV rows into contacts. Mapped columns fill their variable; every other column rides along in `custom_data`
 * under its own heading (but one that looks like the number or the name), so a column the prompt does not use yet
 * is there when it does. A row whose number does not become +E.164 is left out: the campaign would only fail it.
 */
export function buildContacts(
  rows: readonly CsvRow[],
  headers: readonly string[],
  mapping: HeaderMapping,
  defaultCountry: string | null,
): { contacts: Contact[]; skipped: number } {
  const mapped = new Set(Object.values(mapping));
  const phoneFallback = headers.find((header) => mentions(header, PHONE_WORDS));
  const nameFallback = headers.find((header) => mentions(header, NAME_WORDS));

  const contacts: Contact[] = [];
  for (const row of rows) {
    const custom: Record<string, string> = {};
    let phone = "";
    let name = "";
    for (const [variable, header] of Object.entries(mapping)) {
      const value = (row[header] ?? "").trim();
      if (variable === PHONE_VARIABLE) phone = value || phone;
      else if (variable === NAME_VARIABLE) name = value || name;
      else if (value) custom[variable] = value;
    }
    if (!phone && phoneFallback) phone = row[phoneFallback];
    if (!name && nameFallback) name = row[nameFallback];
    for (const header of headers) {
      if (row[header] && !mapped.has(header) && !mentions(header, UNMAPPED_SKIP_WORDS)) custom[header] = row[header];
    }

    const number = normalizeToE164(phone, defaultCountry);
    if (number) contacts.push({ phone_number: number, contact_name: name || null, custom_data: custom });
  }
  return { contacts, skipped: rows.length - contacts.length };
}

/** The contacts as the campaign's file: one JSON object a line. */
export function toNdjson(contacts: readonly Contact[]): string {
  return contacts.map((contact) => JSON.stringify(contact)).join("\n") + "\n";
}

/** What the file itself may not be: more contacts, or more bytes, than one campaign takes. */
export function fileProblem(contacts: number, bytes: number): Problem | null {
  if (contacts > MAX_CONTACTS) {
    return {
      title: "Too many contacts",
      description: `A campaign takes at most ${MAX_CONTACTS.toLocaleString("en-US")} contacts; this file has ${contacts.toLocaleString("en-US")}. Split it into smaller campaigns.`,
    };
  }
  if (bytes > MAX_FILE_BYTES) {
    return { title: "File too large", description: "The contacts come to more than 50 MB. Split them into smaller campaigns." };
  }
  return null;
}

export interface CampaignForm {
  campaignName: string;
  phoneNumberId: string;
  agentId: string;
  headers: string[];
  rows: CsvRow[];
  mapping: HeaderMapping;
  maxRetries: number;
  retryIntervals: number[];
  businessHours: BusinessHoursConfig | null;
  runType: "immediate" | "scheduled";
  scheduledDate: Date | undefined;
}

/** The first thing the form still needs, in the order the dialog asks, or null when it can be sent. */
export function formProblem(form: CampaignForm): Problem | null {
  if (!form.campaignName.trim() || !form.phoneNumberId || !form.agentId) {
    return { title: "Missing required fields", description: "Please fill in all required fields." };
  }
  if (form.campaignName.trim().length > MAX_NAME_CHARS) {
    return { title: "Campaign name too long", description: `A campaign name is at most ${MAX_NAME_CHARS} characters.` };
  }
  if (form.rows.length === 0) return { title: "No contacts", description: "Please upload a CSV file with contacts." };
  const phone = phoneColumnProblem(form.mapping, form.headers, form.rows);
  if (phone) return phone;
  if (form.maxRetries >= 2 && form.retryIntervals.length !== form.maxRetries) {
    return { title: "Invalid retry configuration", description: "Please set intervals for all retry attempts." };
  }
  if (form.maxRetries >= 2 && form.retryIntervals.some((interval) => interval < 1)) {
    return { title: "Invalid retry intervals", description: "All retry intervals must be at least 1 minute." };
  }
  if (form.businessHours) {
    const hours = validateBusinessHours(form.businessHours);
    if (hours.ok === false) return { title: "Invalid business hours", description: hours.errors.slice(0, 2).join("; ") };
  }
  if (form.runType === "scheduled" && !form.scheduledDate) {
    return { title: "Scheduled date required", description: "Please select a date and time for the campaign." };
  }
  return null;
}

/** When a scheduled campaign starts: the picked day at the picked local time (midnight with none), as ISO 8601. */
export function scheduledAt(date: Date, time: string): string {
  const [hours, minutes] = time ? time.split(":").map(Number) : [0, 0];
  const at = new Date(date);
  at.setHours(hours, minutes, 0, 0);
  return at.toISOString();
}

/** The time the schedule picker starts on: a minute from now, local time, as "HH:MM" (at 10:59, 11:00 — not 10:60). */
export function nextMinute(now: Date): string {
  const next = new Date(now.getTime() + 60_000);
  return `${String(next.getHours()).padStart(2, "0")}:${String(next.getMinutes()).padStart(2, "0")}`;
}
